import { CarrierModalStatus } from '~/reducers/carrier-accounts/carrier-accounts.reducer';
import CarrierAccount, { CarrierServiceLevelsEditPayload } from '~/types/CarrierAccount';
import CarrierAccountRegStatus from '~/types/CarrierAccountRegStatus';
import { ActionKeys } from './constants';
import { TrackingReference } from '~/utils/TrackingHelper/types';

export interface CarrierAccountsErrorPayload {
  error: string;
}
export interface SaveCarrierAccountPayload {
  carrierAccount: CarrierAccount;
  trackingReference: TrackingReference;
}

export interface EditCarrierAccountPayload {
  carrierAccount: CarrierAccount;
  id: string;
  trackingReference: TrackingReference;
}

export interface EditCarrierAccountServiceLevelsPayload {
  carrierAccount: CarrierAccount;
  serviceLevelsPayload?: CarrierServiceLevelsEditPayload;
  id: string;
  trackingReference: TrackingReference;
}

export interface FetchCarrierAccountPayload {
  results?: number;
  unsorted?: Maybe<boolean>;
  master?: Maybe<boolean>;
  user?: Maybe<boolean>;
}

export interface FetchCarrierAccountsSuccessPayload {
  carrierAccounts: CarrierAccount[];
}

export interface FetchCarrierAccountRegStatusSuccessPayload {
  carrierAccountRegStatuses: CarrierAccountRegStatus[];
}

// Clear State
export function carrierAccountsClearState() {
  return <const>{
    type: ActionKeys.CARRIER_ACCOUNTS_CLEAR_STATE,
  };
}

export function carrierAccountsClearSubmit() {
  return <const>{
    type: ActionKeys.CARRIER_ACCOUNTS_CLEAR_SUBMIT,
  };
}

// Fetch Carrier Accounts
export function fetchCarrierAccounts(payload?: FetchCarrierAccountPayload) {
  return <const>{
    payload,
    type: ActionKeys.FETCH_CARRIER_ACCOUNTS,
  };
}

export function fetchCarrierAccountsSuccess(payload: FetchCarrierAccountsSuccessPayload) {
  return <const>{
    payload,
    type: ActionKeys.FETCH_CARRIER_ACCOUNTS_SUCCESS,
  };
}

export function fetchCarrierAccountsError(payload: CarrierAccountsErrorPayload) {
  return <const>{
    payload,
    type: ActionKeys.FETCH_CARRIER_ACCOUNTS_ERROR,
  };
}

// Fetch Carrier Account Registration Status
export function fetchCarrierAccountRegStatuses() {
  return <const>{
    type: ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES,
  };
}

export function fetchCarrierAccountRegStatusesSuccess(
  payload: FetchCarrierAccountRegStatusSuccessPayload,
) {
  return <const>{
    payload,
    type: ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES_SUCCESS,
  };
}

export function fetchCarrierAccountRegStatusesError(payload: CarrierAccountsErrorPayload) {
  return <const>{
    payload,
    type: ActionKeys.FETCH_CARRIER_ACCOUNT_REG_STATUSES_ERROR,
  };
}

// Connect
export function connectCarrierAccount(payload: SaveCarrierAccountPayload) {
  return <const>{
    payload,
    type: ActionKeys.CONNECT_CARRIER_ACCOUNT,
  };
}

export function connectCarrierAccountSuccess(
  payload: Omit<SaveCarrierAccountPayload, 'trackingReference'>,
) {
  return <const>{
    payload,
    type: ActionKeys.CONNECT_CARRIER_ACCOUNT_SUCCESS,
  };
}

export function connectCarrierAccountError(payload: CarrierAccountsErrorPayload) {
  return <const>{
    payload,
    type: ActionKeys.CONNECT_CARRIER_ACCOUNT_ERROR,
  };
}

// Edit
export function editCarrierAccount(payload: EditCarrierAccountPayload) {
  return <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT,
  };
}

export function editCarrierAccountSuccess(
  payload: Omit<SaveCarrierAccountPayload, 'trackingReference'>,
) {
  return <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT_SUCCESS,
  };
}

export function editCarrierAccountError(payload: CarrierAccountsErrorPayload) {
  return <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT_ERROR,
  };
}

// Edit Carrier Service Levels
export function editCarrierAccountServiceLevels(payload: EditCarrierAccountServiceLevelsPayload) {
  return <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT_SERVICE_LEVELS,
  };
}

export function editCarrierAccountServiceLevelsSuccess(payload) {
  return <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT_SERVICE_LEVELS_SUCCESS,
  };
}

export function editCarrierAccountServiceLevelsError(payload) {
  return <const>{
    payload,
    type: ActionKeys.EDIT_CARRIER_ACCOUNT_SERVICE_LEVELS_ERROR,
  };
}

// Create
export function createCarrierAccount(payload: SaveCarrierAccountPayload) {
  return <const>{
    payload,
    type: ActionKeys.CREATE_CARRIER_ACCOUNT,
  };
}

export function createCarrierAccountSuccess(payload: SaveCarrierAccountPayload) {
  return <const>{
    payload,
    type: ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS,
  };
}

export function createCarrierAccountError(payload: CarrierAccountsErrorPayload) {
  return <const>{
    payload,
    type: ActionKeys.CREATE_CARRIER_ACCOUNT_ERROR,
  };
}

// Modal
export function toggleCarrierModal(modalStatus: CarrierModalStatus) {
  return <const>{
    payload: {
      modalStatus,
    },
    type: ActionKeys.TOGGLE_CARRIER_MODAL,
  };
}

export type CarrierAccountsActions =
  | ReturnType<typeof carrierAccountsClearState>
  | ReturnType<typeof fetchCarrierAccounts>
  | ReturnType<typeof fetchCarrierAccountsSuccess>
  | ReturnType<typeof fetchCarrierAccountsError>
  | ReturnType<typeof fetchCarrierAccountRegStatuses>
  | ReturnType<typeof fetchCarrierAccountRegStatusesSuccess>
  | ReturnType<typeof fetchCarrierAccountRegStatusesError>
  | ReturnType<typeof connectCarrierAccount>
  | ReturnType<typeof connectCarrierAccountSuccess>
  | ReturnType<typeof connectCarrierAccountError>
  | ReturnType<typeof editCarrierAccount>
  | ReturnType<typeof editCarrierAccountSuccess>
  | ReturnType<typeof editCarrierAccountError>
  | ReturnType<typeof editCarrierAccountServiceLevels>
  | ReturnType<typeof editCarrierAccountServiceLevelsSuccess>
  | ReturnType<typeof editCarrierAccountServiceLevelsError>
  | ReturnType<typeof createCarrierAccount>
  | ReturnType<typeof createCarrierAccountSuccess>
  | ReturnType<typeof createCarrierAccountError>
  | ReturnType<typeof carrierAccountsClearSubmit>
  | ReturnType<typeof toggleCarrierModal>;
